import React, { useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Brain, Sparkles } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const AIPlansPage = () => {
  const { user } = useAuth();
  const [participants, setParticipants] = useState([]);
  const [selectedParticipant, setSelectedParticipant] = useState('');
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingParticipants, setLoadingParticipants] = useState(true);

  React.useEffect(() => {
    fetchParticipants();
  }, []);

  const fetchParticipants = async () => {
    try {
      const response = await api.get('/participants');
      setParticipants(response.data);
    } catch (error) {
      toast.error('Kunde inte hämta deltagare');
    } finally {
      setLoadingParticipants(false);
    }
  };

  const generatePlan = async () => {
    if (!selectedParticipant) {
      toast.error('Välj en deltagare först');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/ai/development-plan', {
        participant_id: selectedParticipant
      });
      setPlan(response.data);
      toast.success('Utvecklingsplan genererad!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunde inte generera plan');
    } finally {
      setLoading(false);
    }
  };

  if (!['super_admin', 'customer_admin'].includes(user?.role)) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <Brain size={64} className="mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-2xl font-heading font-bold mb-2">Åtkomst nekad</h2>
            <p className="text-muted-foreground">Du har inte behörighet att använda AI-analys.</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="ai-plans-page">
        <div>
          <h1 className="text-4xl font-heading font-black tracking-tight flex items-center gap-3">
            <Brain className="text-primary" size={40} />
            AI-Utvecklingsplaner
          </h1>
          <p className="text-muted-foreground mt-2">Generera AI-drivna utvecklingsplaner för deltagare</p>
        </div>

        <Card data-testid="ai-generator-card" className="border-2 border-primary/20">
          <CardHeader className="border-b pb-4 bg-primary/5">
            <CardTitle className="font-heading font-bold text-lg flex items-center gap-2">
              <Sparkles className="text-primary" size={24} />
              Generera Ny Plan
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-bold">Välj Deltagare</label>
              <Select value={selectedParticipant} onValueChange={setSelectedParticipant}>
                <SelectTrigger data-testid="participant-select" className="h-12">
                  <SelectValue placeholder="Välj en deltagare" />
                </SelectTrigger>
                <SelectContent>
                  {loadingParticipants ? (
                    <SelectItem value="loading" disabled>Laddar...</SelectItem>
                  ) : participants.length === 0 ? (
                    <SelectItem value="none" disabled>Inga deltagare tillgängliga</SelectItem>
                  ) : (
                    participants.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <Button
              data-testid="generate-plan-btn"
              onClick={generatePlan}
              disabled={loading || !selectedParticipant}
              className="w-full h-12 text-lg font-bold rounded-full"
            >
              {loading ? (
                <>
                  <Brain className="mr-2 animate-pulse" size={20} />
                  Genererar AI-plan...
                </>
              ) : (
                <>
                  <Sparkles className="mr-2" size={20} />
                  Generera Utvecklingsplan
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {plan && (
          <Card data-testid="ai-plan-result" className="border-2 border-success">
            <CardHeader className="border-b pb-4 bg-success/5">
              <CardTitle className="font-heading font-bold text-lg text-success">
                Genererad Utvecklingsplan
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="prose max-w-none">
                <div className="bg-muted p-6 rounded-lg">
                  <pre className="whitespace-pre-wrap text-sm">{plan.plan}</pre>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {!plan && !loading && (
          <Card className="bg-muted/50">
            <CardContent className="p-12 text-center">
              <Brain size={64} className="mx-auto mb-4 text-muted-foreground" />
              <h3 className="font-heading font-bold text-xl mb-2">AI-Driven Utveckling</h3>
              <p className="text-muted-foreground mb-4">
                Välj en deltagare och generera en personlig utvecklingsplan med hjälp av OpenAI GPT-5.1.
              </p>
              <p className="text-sm text-muted-foreground">
                Planen inkluderar mål, rekommenderade kurser, och strategier för utveckling.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AIPlansPage;
